import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ColorSwitch, type ColorMode } from '@/components/inspector-fields/ColorSwitch/ColorSwitch';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import IconAdd from '@/assets/icons/icon-add.svg?react';
import IconPipettePicker from '@/assets/icons/icon-pipette-picker.svg?react';
import IconDragDropVertical from '@/assets/icons/icon-drag-drop-vertical.svg?react';
import Color from 'color';
import IconPlusSign from '@/assets/icons/icon-plus-sign.svg?react';
import FillPlaceholder from '@/assets/images/icon-imagefill-placeholder.svg';
import type { Gradient, GradientStop } from '@builder4/shared/types/layout';
import { NumberField } from '../NumberField/NumberField';
import {
  isColorReference,
  resolveColorReference,
  rgbaToHexa,
  isValidHex,
} from '@builder4/shared/utils/styleHelpers';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';

export type ColorPickerChangePayload = {
  mode: ColorMode;
  hex: string; // no '#'
  opacity: number; // 0..100
  gradient?: Gradient;
  reference?: string;
};

type PaletteColor = {
  name: string;
  className?: string;
  hex?: string;
  gradient?: Gradient;
  // Optional reference to a global styles token (e.g., "$ref:#/colors/color2")
  reference?: string;
};

// New consolidated palette structure
export interface Palette {
  name: string;
  colors: PaletteColor[];
  readOnly?: boolean;
}

export interface ColorPickerProps {
  className?: string;
  title?: string;
  mode?: ColorMode;
  hex?: string; // without '#'
  opacity?: number; // 0..100
  // Initial gradient when mode is 'gradient'
  gradient?: Gradient;
  hasGradient?: boolean; // whether or not gradients are allowed
  onChange?: (value: ColorPickerChangePayload) => void;
  onChangeComplete?: (value: ColorPickerChangePayload) => void;
  palettes?: Palette[];
  onClose?: () => void;
  // Notify parent that user started dragging the picker so it can detach any auto-scrolling behavior
  onDetachAutoScroll?: () => void;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  className,
  title = 'Color Picker',
  mode = 'solid',
  hex = '9969D7',
  opacity = 100,
  gradient,
  hasGradient = true,
  onChange,
  onChangeComplete,
  palettes = [],
  onClose,
  onDetachAutoScroll,
}) => {
  const activeStyles = useAppSelector(selectActiveGlobalStyle);
  const [palettesState, setPalettesState] = useState<Palette[]>(palettes);
  // Keep internal palettes state in sync with props. Update only when there is a change.
  useEffect(() => {
    if (JSON.stringify(palettesState) !== JSON.stringify(palettes)) {
      setPalettesState(palettes);
    }
  }, [palettes]);

  const [currentMode, setCurrentMode] = useState<ColorMode>('solid');
  const [currentHex, setCurrentHex] = useState<string>(
    hex.replace('#', '').slice(0, 6).toUpperCase(),
  );
  const [currentOpacity, setCurrentOpacity] = useState<number>(opacity);
  const [palette, setPalette] = useState<string>(() => palettes[0]?.name ?? '');
  const [format, setFormat] = useState<'HEX' | 'RGB' | 'HSL'>('HEX');
  const [gradientType, setGradientType] = useState<'Linear' | 'Radial'>('Linear');
  const [gradientAngle, setGradientAngle] = useState<number>(gradient?.angle ?? 0);
  const [stops, setStops] = useState<GradientStop[]>([
    { position: 0, hexa: '#9969D7FF' },
    { position: 1, hexa: '#7FF2FFFF' },
  ]);
  const [activeStopIdx, setActiveStopIdx] = useState<number>(0);
  const [previewStops, setPreviewStops] = useState<GradientStop[]>([
    { position: 0, hexa: '#9969D7FF' },
    { position: 1, hexa: '#7FF2FFFF' },
  ]);
  const [draggingStop, setDraggingStop] = useState<number | null>(null);
  const [eyedropperActive, setEyedropperActive] = useState<boolean>(false);

  // Internal HSV + Alpha state to drive the interactive UI
  const [hue, setHue] = useState<number>(0); // 0..360
  const [sat, setSat] = useState<number>(1); // 0..1
  const [val, setVal] = useState<number>(1); // 0..1
  const [alpha, setAlpha] = useState<number>(currentOpacity / 100); // 0..1

  const [svDragging, setSvDragging] = useState(false);
  const [hueDragging, setHueDragging] = useState(false);
  const [alphaDragging, setAlphaDragging] = useState(false);
  const [activeThumb, setActiveThumb] = useState<'sv' | 'hue' | 'alpha' | null>(null);
  const [addHoverPos, setAddHoverPos] = useState<number | null>(null);
  const [, setHoveredStopIdx] = useState<number | null>(null);

  // Text/segmented inputs state
  const [textValue, setTextValue] = useState<string>('');
  const [rgbValues, setRgbValues] = useState<{ r: string; g: string; b: string }>({
    r: '',
    g: '',
    b: '',
  });
  const [hslValues, setHslValues] = useState<{ h: string; s: string; l: string }>({
    h: '',
    s: '',
    l: '',
  });

  const rootRef = useRef<HTMLDivElement>(null);
  // Unique instance id used for cross-instance coordination (close others when one opens)
  const instanceIdRef = useRef<string>(`cp-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const skipNextGradientBroadcastRef = useRef(false);
  const hueValRef = useRef<number>(0);
  const satValRef = useRef<number>(1);
  const valValRef = useRef<number>(1);
  const alphaValRef = useRef<number>(currentOpacity / 100);
  // Canvas/slider refs
  const canvasRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const alphaRef = useRef<HTMLDivElement>(null);
  const gradientRef = useRef<HTMLDivElement>(null);
  // Focus guards for inputs
  const inputFocusedRef = useRef(false);
  const rgbFocusedRef = useRef<{ r: boolean; g: boolean; b: boolean }>({
    r: false,
    g: false,
    b: false,
  });
  const hslFocusedRef = useRef<{ h: boolean; s: boolean; l: boolean }>({
    h: false,
    s: false,
    l: false,
  });
  // Keep refs in sync with state on every render change
  useEffect(() => {
    hueValRef.current = hue;
  }, [hue]);
  useEffect(() => {
    satValRef.current = sat;
  }, [sat]);
  useEffect(() => {
    valValRef.current = val;
  }, [val]);
  useEffect(() => {
    alphaValRef.current = alpha;
  }, [alpha]);

  const initializeHSV = (hex: string, opacity: number) => {
    const c = Color(`#${(hex || '000000').replace('#', '')}`);
    const hsv = c.hsv().object();
    const h = typeof hsv.h === 'number' && !isNaN(hsv.h) ? hsv.h : 0;
    const s = (typeof hsv.s === 'number' ? hsv.s : 100) / 100; // color returns 0..100
    const v = (typeof hsv.v === 'number' ? hsv.v : 100) / 100;
    setHue(h);
    setSat(Math.max(0, Math.min(1, s)));
    setVal(Math.max(0, Math.min(1, v)));
    setAlpha(Math.max(0, Math.min(1, opacity / 100)));
  };
  // Announce this picker as the active one and close if another picker becomes active
  useEffect(() => {
    // Skip on SSR
    if (typeof window === 'undefined') return;
    const id = instanceIdRef.current;

    const onActivate = (ev: Event) => {
      try {
        const anyEv = ev as unknown as CustomEvent<{ id?: string }>;
        const otherId = anyEv?.detail?.id;
        if (otherId && otherId !== id) {
          // Another ColorPicker instance has been activated -> close this one
          handleBroadcast();
          onClose?.();
        }
      } catch {
        /* noop */
      }
    };

    // Listen for activations from other instances
    window.addEventListener(
      'colorpicker:activate' as unknown as string,
      onActivate as EventListener,
    );

    // Announce self as active so any previously open pickers can close
    try {
      window.dispatchEvent(new CustomEvent('colorpicker:activate', { detail: { id } }));
    } catch {
      /* noop */
    }

    return () => {
      window.removeEventListener(
        'colorpicker:activate' as unknown as string,
        onActivate as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    // Normalize to 6-char hex (strip alpha suffix) to prevent spurious
    // re-initialization when the parent round-trips through rgbaToHexa (8-char).
    setCurrentHex(hex.replace('#', '').slice(0, 6).toUpperCase());
    setCurrentOpacity(opacity);
  }, [hex, opacity]);

  // Initialize and sync HSV/Alpha from current hex/opacity
  useEffect(() => {
    // Skip re-initialization while user is actively dragging a slider to
    // prevent the hue from jumping back when the prop round-trips through
    // a format that loses hue info (e.g. achromatic colors).
    if (activeThumb) return;
    initializeHSV(currentHex, currentOpacity);
  }, [currentHex, currentOpacity]);

  // If starting in gradient mode with an initial gradient, initialize internal gradient state
  // on each gradient change, useeffect gets called, need to restrict while dragging
  // Initialize internal gradient state when:
  // 1. Mode changes to gradient
  // 2. Gradient changes due to undo/redo operations
  // Note: Prevents unnecessary updates during drag operations

  useEffect(() => {
    setCurrentMode(mode);
    if (mode !== 'gradient') return;
    if (!gradient || !Array.isArray(gradient.stops) || gradient.stops.length < 2) return;
    // Switch mode internally and load provided gradient
    setGradientType(gradient.type);
    setGradientAngle(gradient.angle ?? 0);
    const sanitizedStops: GradientStop[] = gradient.stops.map((s) => ({
      position: Math.max(0, Math.min(1, s.position ?? 0)),
      hexa: (() => {
        try {
          return Color(s.hexa).hexa();
        } catch {
          return '#000000FF';
        }
      })(),
    }));
    setStops(sanitizedStops);
    setPreviewStops(sanitizedStops);
  }, [mode, gradient]);

  const pureHueColor = useMemo(() => Color.hsv(hue, 100, 100), [hue]);
  const currentColor = useMemo(
    () => Color.hsv(hue, sat * 100, val * 100).alpha(alpha),
    [hue, sat, val, alpha],
  );
  const createValue = (
    next?: Partial<{
      mode: ColorMode;
      hex: string;
      opacity: number;
      reference?: string;
      gradient: Gradient;
      angle: number;
    }>,
  ) => {
    const modeToSend = next?.mode ?? currentMode;
    const computedHex = (
      next?.hex ??
      Color.hsv(hue, sat * 100, val * 100)
        .alpha(alpha)
        .hex()
    )
      .replace('#', '')
      .slice(0, 6)
      .toUpperCase();
    const value: {
      mode: ColorMode;
      hex: string;
      opacity: number;
      reference?: string;
      gradient?: Gradient;
    } = {
      mode: modeToSend,
      // Compute hex from HSV state to avoid use-before-define on computed values
      hex: computedHex,
      opacity: next?.opacity ?? Math.round(alpha * 100),
    };
    if (typeof next?.reference !== 'undefined') {
      value.reference = next.reference;
    }
    if (modeToSend === 'gradient') {
      value.gradient = next?.gradient ?? {
        type: gradientType,
        angle: next?.angle ?? gradientAngle,
        stops: stops.map((s) => ({
          position: s.position,
          hexa: Color(s.hexa).hexa().toUpperCase(),
        })),
      };
    }
    return value;
  };
  const handleBroadcast = (
    next?: Partial<{
      mode: ColorMode;
      hex: string;
      opacity: number;
      reference?: string;
      gradient: Gradient;
      angle: number;
    }>,
  ) => {
    const value = createValue(next);
    onChange?.(value);
  };
  const handleBroadcastComplete = (
    next?: Partial<{
      mode: ColorMode;
      hex: string;
      opacity: number;
      reference?: string;
      gradient: Gradient;
      angle: number;
    }>,
  ) => {
    const value = createValue(next);
    onChangeComplete?.(value);
  };

  const resolveReferenceColor = (ref: string | undefined): string => {
    if (!ref) return '';
    const resolved = resolveColorReference(ref, activeStyles.colors);
    if (resolved) {
      return `#${rgbaToHexa(resolved)}`;
    }
    return '';
  };

  // Apply picked color (HEX like #RRGGBB) to internal state and broadcast
  const applyPickedColor = (hexStr: string) => {
    try {
      const raw = hexStr.replace('#', '').slice(0, 6).toUpperCase();
      const c = Color(`#${raw}`);
      const hsv = c.hsv().object();
      const h = typeof hsv.h === 'number' && !isNaN(hsv.h) ? hsv.h : 0;
      const s = (typeof hsv.s === 'number' ? hsv.s : 100) / 100;
      const v = (typeof hsv.v === 'number' ? hsv.v : 100) / 100;
      setHue(h);
      setSat(Math.max(0, Math.min(1, s)));
      setVal(Math.max(0, Math.min(1, v)));
      setCurrentHex(raw);
      // keep current alpha; update gradient active stop if needed
      if (currentMode === 'solid') {
        handleBroadcastComplete({ hex: raw });
      } else if (currentMode === 'gradient') {
        // Update active gradient stop color
        updateActiveStopColor(h, Math.max(0, Math.min(1, s)), Math.max(0, Math.min(1, v)), alpha);
        // Broadcast gradient change
        const freshHexa = c.alpha(alpha).hexa();
        const freshStops = stops.map((s, idx) =>
          idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
        );

        skipNextGradientBroadcastRef.current = true;
        handleBroadcastComplete({
          mode: 'gradient',
          gradient: {
            angle: gradientAngle,
            type: gradientType,
            stops: freshStops.map((s) => ({
              position: s.position,
              hexa: Color(s.hexa).hexa().toUpperCase(),
            })),
          },
        });
      }
    } catch {
      // ignore
    }
  };

  // Eyedropper behavior: Prefer native EyeDropper API, fallback to click-pick element color
  useEffect(() => {
    if (!eyedropperActive) return;

    let cleaning = false;

    const cleanup = () => {
      if (cleaning) return;
      cleaning = true;
      document.removeEventListener('click', handleDocClick, true);
      document.removeEventListener('keydown', handleKeyDown);
      try {
        // restore cursor if we modified it
        if (document.body) document.body.style.cursor = '';
      } catch {
        /* noop */
      }
    };

    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        cleanup();
        setEyedropperActive(false);
      }
    };

    const handleDocClick = (ev: MouseEvent) => {
      // Ignore clicks inside the color picker UI itself
      if (rootRef.current && ev.target instanceof Node && rootRef.current.contains(ev.target)) {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      const el = ev.target as HTMLElement | null;
      let colorStr = '';
      try {
        const cs = el ? getComputedStyle(el) : null;
        // Prefer background-color if not transparent, otherwise use text color
        const bg = cs?.backgroundColor ?? '';
        if (bg && bg !== 'transparent' && !/rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(bg)) {
          colorStr = bg;
        } else {
          colorStr = cs?.color ?? '#000000';
        }
      } catch {
        colorStr = '#000000';
      }
      try {
        const picked = Color(colorStr).hex();
        applyPickedColor(picked);
      } catch {
        // ignore
      }
      cleanup();
      setEyedropperActive(false);
    };

    // Try native EyeDropper API first
    type EyeDropperResult = { sRGBHex: string };
    interface EyeDropper {
      open: () => Promise<EyeDropperResult>;
    }
    interface EyeDropperConstructor {
      new (): EyeDropper;
    }
    const EyeDropperCtor = (window as Window & { EyeDropper?: EyeDropperConstructor }).EyeDropper;
    if (typeof EyeDropperCtor === 'function') {
      const eye = new EyeDropperCtor();
      let usingFallback = false;
      eye
        .open()
        .then(({ sRGBHex }) => {
          if (sRGBHex) applyPickedColor(sRGBHex);
          // success path: deactivate
          setEyedropperActive(false);
        })
        .catch(() => {
          // user canceled or not allowed -> fall back to manual picker for this activation
          usingFallback = true;
          try {
            document.body.style.cursor = 'crosshair';
          } catch {
            /* noop */
          }
          document.addEventListener('click', handleDocClick, true);
          document.addEventListener('keydown', handleKeyDown);
          return;
        })
        .finally(() => {
          // If we didn't fall back, effect will clean up on deactivation
          if (!usingFallback) {
            // nothing else
          }
        });
      // We return a cleanup that ensures any potential listeners are removed
      return () => cleanup();
    }

    // Fallback: manual click pick
    try {
      document.body.style.cursor = 'crosshair';
    } catch {
      /* noop */
    }
    document.addEventListener('click', handleDocClick, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => cleanup();
  }, [eyedropperActive]);

  // When switching to gradient or selecting another stop, sync HSV/alpha to active stop color
  useEffect(() => {
    if (currentMode !== 'gradient') return;
    const s = stops[activeStopIdx];
    if (!s) return;
    try {
      const c = Color(s.hexa);
      const hsv = c.hsv().object();
      const h = typeof hsv.h === 'number' && !isNaN(hsv.h) ? hsv.h : 0;
      const sv = (typeof hsv.s === 'number' ? hsv.s : 100) / 100;
      const vv = (typeof hsv.v === 'number' ? hsv.v : 100) / 100;
      setHue(h);
      setSat(Math.max(0, Math.min(1, sv)));
      setVal(Math.max(0, Math.min(1, vv)));
      setAlpha(Math.max(0, Math.min(1, c.alpha())));
    } catch {
      // ignore
    }
  }, [currentMode, activeStopIdx, stops]);

  // Helper: update active gradient stop color when in gradient mode
  const updateActiveStopColor = (h: number, s: number, v: number, a: number) => {
    if (currentMode !== 'gradient') return;
    const newHexa = Color.hsv(h, s * 100, v * 100)
      .alpha(a)
      .hexa();
    setStops((prev) => {
      const copy = [...prev];
      if (copy[activeStopIdx]) copy[activeStopIdx] = { ...copy[activeStopIdx], hexa: newHexa };
      return copy;
    });
    setPreviewStops((prev) => {
      const copy = [...prev];
      if (copy[activeStopIdx]) copy[activeStopIdx] = { ...copy[activeStopIdx], hexa: newHexa };
      return copy;
    });
  };

  // Add a new stop at position p (0..1), color from current HSV/alpha
  const addStopAt = (p: number) => {
    const pos = Math.max(0, Math.min(1, p));
    const hexa = Color.hsv(hue, sat * 100, val * 100)
      .alpha(alpha)
      .hexa();
    const nextStop: GradientStop = { position: pos, hexa };
    // Compute merged stops first to broadcast the latest value synchronously
    const merged = [...stops, nextStop].sort((a, b) => a.position - b.position);
    const idx = merged.findIndex((s) => s === nextStop);
    setStops(merged);
    setActiveStopIdx(idx >= 0 ? idx : 0);
    setPreviewStops(merged);
    // Explicitly broadcast with fresh gradient data and skip the idle effect re-broadcast
    skipNextGradientBroadcastRef.current = true;
    handleBroadcast({
      mode: 'gradient',
      gradient: {
        type: gradientType,
        angle: gradientAngle,
        stops: merged.map((s) => ({
          position: s.position,
          hexa: Color(s.hexa).hexa().toUpperCase(),
        })),
      },
    });
  };

  // Keyboard Delete/Backspace to remove the active stop (min 2 stops kept)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (currentMode !== 'gradient') return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Prevent the Delete/Backspace key from propagating to parent handlers (e.g., deleting the container)
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      // Calculate new stops array outside of setter to broadcast the change
      const idx = Math.max(0, Math.min(stops.length - 1, activeStopIdx));
      if (stops.length <= 2) return;
      const next = stops.filter((_, i) => i !== idx);
      const newActiveIdx = Math.max(0, idx - 1);
      setStops(next);
      setPreviewStops(next);
      setActiveStopIdx(newActiveIdx);
      // Broadcast the change to update the canvas
      skipNextGradientBroadcastRef.current = true;
      handleBroadcastComplete({
        mode: 'gradient',
        gradient: {
          type: gradientType,
          angle: gradientAngle,
          stops: next.map((s) => ({
            position: s.position,
            hexa: Color(s.hexa).hexa().toUpperCase(),
          })),
        },
      });
    };
    // Use capture phase to intercept Delete before other handlers
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [currentMode, activeStopIdx, stops, gradientType, gradientAngle]);

  // Ensure selected palette exists when palettes change
  useEffect(() => {
    if (palettesState.length === 0) {
      setPalette('');
    }

    const names = palettesState.map((p) => p.name);
    if (!names.includes(palette)) {
      setPalette(names[0] ?? '');
    }
  }, [palettesState]);

  // Rule: disable add-to-palette for palettes explicitly marked as readOnly
  const disabledPalettes = useMemo(
    () => palettesState.filter((p) => p.readOnly).map((p) => p.name),
    [palettesState],
  );
  const canAddToSelectedPalette = useMemo(() => {
    return !disabledPalettes.includes(palette);
  }, [disabledPalettes, palette]);

  // Determine how many colors to render for the selected palette (cap for non-current page)
  const visiblePaletteColors = useMemo(() => {
    const pal = palettesState.find((p) => p.name === palette);
    if (!pal) return [] as PaletteColor[];
    const list = pal.colors || [];
    return list;
  }, [palettesState, palette]);

  // Helpers for deduplication
  const normalizeHexa = (h: string) => {
    try {
      return Color(h).hexa().toUpperCase(); // #RRGGBBAA
    } catch {
      return (h || '').toUpperCase();
    }
  };
  const sameSolid = (a?: string, b?: string) => {
    if (!a || !b) return false;
    return normalizeHexa(a) === normalizeHexa(b);
  };
  const normalizeStops = (arr: GradientStop[]) =>
    arr.map((s) => ({
      hexa: normalizeHexa(s.hexa),
      position: Math.round((s.position ?? 0) * 1000) / 1000, // 3 decimals tolerance
    }));
  const sameGradient = (
    a?: { type: 'Linear' | 'Radial'; stops: GradientStop[] },
    b?: { type: 'Linear' | 'Radial'; stops: GradientStop[] },
  ) => {
    if (!a || !b) return false;
    if (a.type !== b.type) return false;
    const sa = normalizeStops(a.stops);
    const sb = normalizeStops(b.stops);
    if (sa.length !== sb.length) return false;
    for (let i = 0; i < sa.length; i++) {
      if (sa[i].hexa !== sb[i].hexa) return false;
      if (sa[i].position !== sb[i].position) return false;
    }
    return true;
  };

  // Helpers to format/parse by format
  const formatColor = (c: InstanceType<typeof Color>, f: 'HEX' | 'RGB' | 'HSL') => {
    if (f === 'HEX') return c.hex().replace('#', '').toUpperCase();
    if (f === 'RGB') {
      const { r, g, b } = c.rgb().object();
      return `${Math.round(r ?? 0)}, ${Math.round(g ?? 0)}, ${Math.round(b ?? 0)}`;
    }
    const hsl = c.hsl().object();
    const h = Math.round(hsl.h ?? 0);
    const s = Math.round(hsl.s ?? 0);
    const l = Math.round(hsl.l ?? 0);
    return `${h}, ${s}%, ${l}%`;
  };
  const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

  const applyColor = (c: InstanceType<typeof Color>) => {
    const hsv = c.hsv().object();
    const h = typeof hsv.h === 'number' && !isNaN(hsv.h) ? hsv.h : 0;
    const s = (typeof hsv.s === 'number' ? hsv.s : 100) / 100;
    const v = (typeof hsv.v === 'number' ? hsv.v : 100) / 100;
    setHue(h);
    setSat(clamp(s, 0, 1));
    setVal(clamp(v, 0, 1));
    setCurrentHex(c.hex().replace('#', '').toUpperCase());
    updateActiveStopColor(h, clamp(s, 0, 1), clamp(v, 0, 1), alpha);
    const finalHex = c.hex().replace('#', '').toUpperCase();
    const finalOpacity = Math.round(alphaValRef.current * 100);
    if (currentMode === 'gradient') {
      const freshHexa = c.hexa();
      const freshStops = stops.map((s, idx) =>
        idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
      );
      skipNextGradientBroadcastRef.current = true;
      handleBroadcastComplete({
        mode: 'gradient',
        gradient: {
          type: gradientType,
          angle: gradientAngle,
          stops: freshStops.map((s) => ({
            position: s.position,
            hexa: Color(s.hexa).hexa().toUpperCase(),
          })),
        },
      });
    } else {
      // Solid color mode: broadcast the final color
      handleBroadcastComplete({
        mode: 'solid',
        hex: finalHex,
        opacity: finalOpacity,
      });
    }
  };

  const tryApplyText = (raw: string) => {
    if (format === 'HEX') {
      const v = raw.replace('#', '').trim().toUpperCase();
      const valid = isValidHex(v);
      if (!valid) return;
      try {
        applyColor(Color(`#${v}`));
      } catch {
        /* ignore */
      }
      return;
    }
    if (format === 'RGB') {
      const m = raw.match(/^\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*$/);
      if (!m) return;
      const r = clamp(parseInt(m[1], 10), 0, 255);
      const g = clamp(parseInt(m[2], 10), 0, 255);
      const b = clamp(parseInt(m[3], 10), 0, 255);
      try {
        applyColor(Color.rgb(r, g, b));
      } catch {
        /* ignore */
      }
      return;
    }
    // HSL
    const m = raw.match(/^\s*(\d{1,3})\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*$/);
    if (!m) return;
    const h = clamp(parseInt(m[1], 10), 0, 360);
    const s = clamp(parseInt(m[2], 10), 0, 100);
    const l = clamp(parseInt(m[3], 10), 0, 100);
    try {
      applyColor(Color.hsl(h, s, l));
    } catch {
      /* ignore */
    }
  };

  const tryApplyRgb = (rStr: string, gStr: string, bStr: string) => {
    const num = (s: string) => (/^\d{1,3}$/.test(s) ? clamp(parseInt(s, 10), 0, 255) : null);
    const r = num(rStr),
      g = num(gStr),
      b = num(bStr);
    if (r === null || g === null || b === null) return;
    try {
      applyColor(Color.rgb(r, g, b));
    } catch {
      /* noop */
    }
  };

  const tryApplyHsl = (hStr: string, sStr: string, lStr: string) => {
    const numH = (s: string) => (/^\d{1,3}$/.test(s) ? clamp(parseInt(s, 10), 0, 360) : null);
    const numSL = (s: string) => (/^\d{1,3}$/.test(s) ? clamp(parseInt(s, 10), 0, 100) : null);
    const h = numH(hStr),
      s = numSL(sStr),
      l = numSL(lStr);
    if (h === null || s === null || l === null) return;
    try {
      applyColor(Color.hsl(h, s, l));
    } catch {
      /* noop */
    }
  };

  // Sync editable text (HEX) unless focused
  useEffect(() => {
    if (!inputFocusedRef.current) {
      setTextValue(formatColor(currentColor, 'HEX'));
    }
  }, [currentColor]);

  // Sync segmented inputs from current color unless the user is typing in them
  useEffect(() => {
    if (format === 'RGB') {
      const anyFocused = Object.values(rgbFocusedRef.current).some(Boolean);
      if (!anyFocused) {
        const { r = 0, g = 0, b = 0 } = currentColor.rgb().object();
        setRgbValues({
          r: String(Math.round(r)),
          g: String(Math.round(g)),
          b: String(Math.round(b)),
        });
      }
    } else if (format === 'HSL') {
      const anyFocused = Object.values(hslFocusedRef.current).some(Boolean);
      if (!anyFocused) {
        const hsl = currentColor.hsl().object();
        const h = Math.round(hsl.h ?? 0);
        const s = Math.round(hsl.s ?? 0);
        const l = Math.round(hsl.l ?? 0);
        setHslValues({ h: String(h), s: String(s), l: String(l) });
      }
    }
  }, [currentColor, format]);

  const handleSwitchMode = (m: ColorMode) => {
    setCurrentMode(m);
    // Reset to first stop when manually switching to gradient mode
    if (m === 'gradient') {
      setActiveStopIdx(0);
    }
    //hex is the current value of the node property (backgroundColor, borderColor, etc.)
    const data: { mode: ColorMode; hex: string; gradient?: Gradient } = { mode: m, hex };
    if (m === 'gradient') {
      data.gradient = gradient;
    }
    handleBroadcastComplete(data);
    if (m === 'solid') {
      initializeHSV(hex, currentOpacity);
    }
  };

  return (
    <div
      ref={rootRef}
      className={clsx(
        'bg-neutral-0 inline-flex w-full max-w-[244px] min-w-[244px] flex-col items-center justify-center gap-3 rounded-lg p-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100',
        className,
      )}
      onMouseDown={() => setActiveThumb(null)}
      //   style={{ width: 244, height: currentMode === 'gradient' ? 446 : 346 }}
    >
      {/* Header */}
      <div className="inline-flex items-center justify-between self-stretch">
        <div className="flex items-center justify-start gap-1">
          <IconDragDropVertical
            className="colorpicker-drag-handle h-5 w-5 cursor-grab text-neutral-500"
            onMouseDown={() => onDetachAutoScroll?.()}
          />
          <div className="text-heading-ts leading-tight font-semibold text-neutral-900">
            {title}
          </div>
        </div>
        <IconSimpleButton
          icon={IconClose}
          onClick={() => {
            onClose?.();
          }}
          size={24}
        />
      </div>

      {/* Mode Switch */}
      <div className="inline-flex items-center justify-between self-stretch">
        {/* Eyedropper control: default / hover / active-selected */}
        <button
          type="button"
          aria-pressed={eyedropperActive}
          onClick={() => setEyedropperActive((v) => !v)}
          title="Color Picker"
          className={clsx(
            'group flex h-8 w-8 cursor-pointer flex-col items-center justify-center rounded-md shadow-none',
            !eyedropperActive && 'bg-neutral-0 hover:bg-neutral-100',
            eyedropperActive && 'bg-transparent outline outline-indigo-500',
          )}
        >
          <IconPipettePicker
            className={clsx('h-4 w-4', eyedropperActive ? 'text-indigo-500' : 'text-neutral-500')}
          />
        </button>
        <ColorSwitch value={currentMode} onChange={handleSwitchMode} canChange={hasGradient} />
      </div>

      {/* Canvas and sliders */}
      <div className="flex items-center justify-between gap-2 self-stretch">
        {/* SV Canvas */}
        <div
          ref={canvasRef}
          className="relative h-[138px] w-[180px] rounded-md outline outline-neutral-100 select-none"
          onMouseDown={(e) => {
            e.stopPropagation();
            setActiveThumb('sv');
            setSvDragging(true);
            const move = (ev: MouseEvent) => {
              const el = canvasRef.current;
              if (!el) return;
              const rect = el.getBoundingClientRect();
              const x = Math.max(0, Math.min(rect.width, (ev.clientX ?? 0) - rect.left));
              const y = Math.max(0, Math.min(rect.height, (ev.clientY ?? 0) - rect.top));
              const s = x / rect.width; // 0..1
              const v = 1 - y / rect.height; // 0..1 (top=1)
              setSat(s);
              setVal(v);
              // keep live refs updated during dragging
              satValRef.current = s;
              valValRef.current = v;
              // Live update active stop color in gradient mode
              updateActiveStopColor(hueValRef.current, s, v, alphaValRef.current);
              // Broadcast live gradient changes with fresh data
              const finalColor = Color.hsv(hueValRef.current, s * 100, v * 100).alpha(
                alphaValRef.current,
              );
              const finalHex = finalColor.hex().replace('#', '').toUpperCase();
              const finalOpacity = Math.round(alphaValRef.current * 100);
              const freshHexa = finalColor.hexa();

              if (currentMode === 'gradient') {
                const freshStops = stops.map((s, idx) =>
                  idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
                );
                handleBroadcast({
                  mode: 'gradient',
                  gradient: {
                    type: gradientType,
                    angle: gradientAngle,
                    stops: freshStops.map((s) => ({
                      position: s.position,
                      hexa: Color(s.hexa).hexa().toUpperCase(),
                    })),
                  },
                });
              } else if (currentMode === 'solid') {
                // Solid color mode: broadcast the final color
                handleBroadcast({
                  mode: 'solid',
                  hex: finalHex,
                  opacity: finalOpacity,
                });
              }
            };
            const up = () => {
              document.removeEventListener('mousemove', move);
              document.removeEventListener('mouseup', up);
              setSvDragging(false);
              // Clear active thumb on release to allow smooth transition back to idle state
              setActiveThumb(null);
              // Compute final color and opacity from refs to avoid stale closure values
              const finalColor = Color.hsv(
                hueValRef.current,
                satValRef.current * 100,
                valValRef.current * 100,
              ).alpha(alphaValRef.current);
              const finalHex = finalColor.hex().replace('#', '').toUpperCase();
              const finalOpacity = Math.round(alphaValRef.current * 100);
              setCurrentHex(finalHex);
              setCurrentOpacity(finalOpacity);
              skipNextGradientBroadcastRef.current = true;
              if (currentMode === 'gradient') {
                const freshHexa = finalColor.hexa();
                const freshStops = stops.map((s, idx) =>
                  idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
                );
                skipNextGradientBroadcastRef.current = true;
                handleBroadcastComplete({
                  mode: 'gradient',
                  gradient: {
                    type: gradientType,
                    angle: gradientAngle,
                    stops: freshStops.map((s) => ({
                      position: s.position,
                      hexa: Color(s.hexa).hexa().toUpperCase(),
                    })),
                  },
                });
              } else {
                // Solid color mode: broadcast the final color
                handleBroadcastComplete({
                  mode: 'solid',
                  hex: finalHex,
                  opacity: finalOpacity,
                });
              }
            };
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
            move(e.nativeEvent as unknown as MouseEvent);
          }}
          style={{
            backgroundImage: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, ${pureHueColor.hex()})`,
          }}
        >
          {/* SV handle */}
          <div
            className={clsx(
              'pointer-events-none absolute h-3 w-3 rounded-full outline outline-white',
              svDragging || activeThumb === 'sv'
                ? 'shadow-[0px_0px_6px_0px_rgba(0,0,0,0.60)] outline-3'
                : 'shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] outline-1',
            )}
            style={{
              left: sat * (180 - 12),
              top: (1 - val) * (138 - 12),
            }}
          />
        </div>

        {/* Hue + Alpha sliders (both vertical) */}
        <div className="flex items-center gap-3">
          {/* Hue (vertical) */}
          <div
            ref={hueRef}
            className="relative h-[138px] w-3 rounded-full outline outline-neutral-100 select-none"
            style={{
              background:
                'linear-gradient(to bottom, rgb(255,0,0) 0%, rgb(255,255,0) 17%, rgb(0,255,0) 33%, rgb(0,255,255) 50%, rgb(0,0,255) 67%, rgb(255,0,255) 83%, rgb(255,0,0) 100%)',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setActiveThumb('hue');
              setHueDragging(true);
              const move = (ev: MouseEvent) => {
                const el = hueRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const y = Math.max(0, Math.min(rect.height, (ev.clientY ?? 0) - rect.top));
                const h = (y / rect.height) * 360; // 0..360
                setHue(h);
                // keep live refs updated during dragging
                hueValRef.current = h;
                updateActiveStopColor(h, satValRef.current, valValRef.current, alphaValRef.current);
                // Broadcast live gradient changes with fresh data
                const finalColor = Color.hsv(
                  h,
                  satValRef.current * 100,
                  valValRef.current * 100,
                ).alpha(alphaValRef.current);

                const freshHexa = finalColor.hexa();
                const finalOpacity = Math.round(alphaValRef.current * 100);
                const finalHex = finalColor.hex().replace('#', '').toUpperCase();
                if (currentMode === 'gradient') {
                  const freshStops = stops.map((s, idx) =>
                    idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
                  );
                  handleBroadcast({
                    mode: 'gradient',
                    gradient: {
                      type: gradientType,
                      angle: gradientAngle,
                      stops: freshStops.map((s) => ({
                        position: s.position,
                        hexa: Color(s.hexa).hexa().toUpperCase(),
                      })),
                    },
                  });
                } else if (currentMode === 'solid') {
                  // Solid color mode: broadcast the final color
                  handleBroadcast({
                    mode: 'solid',
                    hex: finalHex,
                    opacity: finalOpacity,
                  });
                }
              };
              const up = () => {
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
                setHueDragging(false);
                // Clear active thumb on release to allow smooth transition back to idle state
                setActiveThumb(null);
                // Compute final color and opacity from refs to avoid stale closure values
                const finalColor = Color.hsv(
                  hueValRef.current,
                  satValRef.current * 100,
                  valValRef.current * 100,
                ).alpha(alphaValRef.current);
                const finalHex = finalColor.hex().replace('#', '').toUpperCase();
                const finalOpacity = Math.round(alphaValRef.current * 100);
                setCurrentHex(finalHex);
                setCurrentOpacity(finalOpacity);
                skipNextGradientBroadcastRef.current = true;
                if (currentMode === 'gradient') {
                  const freshHexa = finalColor.hexa();
                  const freshStops = stops.map((s, idx) =>
                    idx === activeStopIdx ? { ...s, hexa: freshHexa } : s,
                  );
                  skipNextGradientBroadcastRef.current = true;
                  handleBroadcastComplete({
                    mode: 'gradient',
                    gradient: {
                      type: gradientType,
                      angle: gradientAngle,
                      stops: freshStops.map((s) => ({
                        position: s.position,
                        hexa: Color(s.hexa).hexa().toUpperCase(),
                      })),
                    },
                  });
                } else {
                  // Solid color mode: broadcast the final color
                  handleBroadcastComplete({
                    mode: 'solid',
                    hex: finalHex,
                    opacity: finalOpacity,
                  });
                }
              };
              document.addEventListener('mousemove', move);
              document.addEventListener('mouseup', up);
              move(e.nativeEvent as unknown as MouseEvent);
            }}
          >
            {/* Hue handle */}
            <div
              className={clsx(
                'pointer-events-none absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full outline outline-white',
                hueDragging || activeThumb === 'hue'
                  ? 'shadow-[0px_0px_6px_0px_rgba(0,0,0,0.60)] outline-3'
                  : 'shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] outline-1',
              )}
              style={{ top: (hue / 360) * (138 - 12) }}
            />
          </div>

          {/* Alpha (vertical) */}
          <div
            ref={alphaRef}
            className="relative h-[138px] w-3 rounded-full outline outline-neutral-100 select-none"
            style={{
              backgroundImage: `linear-gradient(to bottom, ${currentColor.alpha(1).hexa()}, ${currentColor.alpha(0).hexa()})`,
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setActiveThumb('alpha');
              setAlphaDragging(true);
              const move = (ev: MouseEvent) => {
                const el = alphaRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const y = Math.max(0, Math.min(rect.height, (ev.clientY ?? 0) - rect.top));
                const a = 1 - y / rect.height; // 0..1 (top=1)
                setAlpha(a);
                // keep live refs updated during dragging
                alphaValRef.current = a;
                const finalOpacity = Math.round(a * 100);
                setCurrentOpacity(finalOpacity);

                if (currentMode === 'gradient') {
                  setStops((prev) => {
                    const copy = [...prev];
                    const col = Color(copy[activeStopIdx].hexa).alpha(a);
                    copy[activeStopIdx] = { ...copy[activeStopIdx], hexa: col.hexa() };
                    return copy;
                  });
                  setPreviewStops((prev) => {
                    const copy = [...prev];
                    const col = Color(copy[activeStopIdx].hexa).alpha(a);
                    copy[activeStopIdx] = { ...copy[activeStopIdx], hexa: col.hexa() };
                    return copy;
                  });
                  // Broadcast live gradient changes with fresh data
                  const freshStops = stops.map((s, idx) =>
                    idx === activeStopIdx ? { ...s, hexa: Color(s.hexa).alpha(a).hexa() } : s,
                  );
                  handleBroadcast({
                    mode: 'gradient',
                    gradient: {
                      type: gradientType,
                      angle: gradientAngle,
                      stops: freshStops.map((s) => ({
                        position: s.position,
                        hexa: Color(s.hexa).hexa().toUpperCase(),
                      })),
                    },
                  });
                } else if (currentMode === 'solid') {
                  // Solid color mode: broadcast the final color
                  handleBroadcast({
                    mode: 'solid',
                    opacity: finalOpacity,
                  });
                }
              };
              const up = () => {
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
                setAlphaDragging(false);
                setActiveThumb(null);
                // Compute final color and opacity from refs to avoid stale closure values
                const finalColor = Color.hsv(
                  hueValRef.current,
                  satValRef.current * 100,
                  valValRef.current * 100,
                ).alpha(alphaValRef.current);
                const finalHex = finalColor.hex().replace('#', '').toUpperCase();
                const finalOpacity = Math.round(alphaValRef.current * 100);
                setCurrentHex(finalHex);
                setCurrentOpacity(finalOpacity);
                if (currentMode === 'gradient') {
                  // Broadcast live gradient changes with fresh data
                  const freshStops = stops.map((s, idx) =>
                    idx === activeStopIdx
                      ? { ...s, hexa: Color(s.hexa).alpha(alphaValRef.current).hexa() }
                      : s,
                  );
                  skipNextGradientBroadcastRef.current = true;
                  handleBroadcastComplete({
                    mode: 'gradient',
                    gradient: {
                      type: gradientType,
                      angle: gradientAngle,
                      stops: freshStops.map((s) => ({
                        position: s.position,
                        hexa: Color(s.hexa).hexa().toUpperCase(),
                      })),
                    },
                  });
                } else {
                  // Solid color mode: broadcast the final color
                  handleBroadcastComplete({
                    mode: 'solid',
                    hex: finalHex,
                    opacity: finalOpacity,
                  });
                }
              };
              document.addEventListener('mousemove', move);
              document.addEventListener('mouseup', up);
              move(e.nativeEvent as unknown as MouseEvent);
            }}
          >
            {/* Alpha handle */}
            <div
              className={clsx(
                'pointer-events-none absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full outline outline-white',
                alphaDragging || activeThumb === 'alpha'
                  ? 'shadow-[0px_0px_6px_0px_rgba(0,0,0,0.60)] outline-3'
                  : 'shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] outline-1',
              )}
              style={{ top: (1 - alpha) * (138 - 12) }}
            />
          </div>
        </div>
      </div>

      {/* Gradient stops editor (only in gradient mode) */}
      {currentMode === 'gradient' && (
        <div className="flex items-center gap-2 self-stretch">
          <div
            ref={gradientRef}
            className="relative h-3 flex-1 cursor-crosshair rounded-full outline outline-neutral-100 select-none"
            style={{
              background:
                gradientType === 'Linear'
                  ? `linear-gradient(90deg, ${previewStops
                      .map((s) => `${Color(s.hexa).string()} ${Math.round(s.position * 100)}%`)
                      .join(', ')})`
                  : `radial-gradient(circle, ${previewStops
                      .map((s) => `${Color(s.hexa).string()} ${Math.round(s.position * 100)}%`)
                      .join(', ')})`,
            }}
            onMouseMove={(e) => {
              const el = gradientRef.current;
              if (!el) return;
              const rect = el.getBoundingClientRect();
              const x = Math.max(0, Math.min(rect.width, (e.clientX ?? 0) - rect.left));
              setAddHoverPos(x / rect.width);
            }}
            onMouseLeave={() => setAddHoverPos(null)}
            onMouseDown={(e) => {
              // clicking empty area selects nearest stop
              const el = gradientRef.current;
              if (!el) return;
              const rect = el.getBoundingClientRect();
              const x = Math.max(0, Math.min(rect.width, (e.clientX ?? 0) - rect.left));
              const pos = x / rect.width;
              let nearest = 0;
              let best = Infinity;
              stops.forEach((s, i) => {
                const d = Math.abs(s.position - pos);
                if (d < best) {
                  best = d;
                  nearest = i;
                }
              });
              setActiveStopIdx(nearest);
            }}
          >
            {/* Plus marker that follows cursor to add a new stop; hidden near existing stops to avoid overlap */}
            {(() => {
              if (addHoverPos === null || draggingStop !== null) return null;
              const w = gradientRef.current?.clientWidth ?? 0;
              const minPx = w
                ? Math.min(...stops.map((s) => Math.abs(s.position - addHoverPos) * w))
                : Infinity;
              const show = minPx > 10; // hide if too close to an existing thumb
              if (!show) return null;
              return (
                <button
                  type="button"
                  aria-label="Add stop"
                  className="absolute top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)]"
                  style={{ left: `calc(${addHoverPos} * (100% - 16px))` }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    addStopAt(addHoverPos);
                  }}
                  title="Add color stop"
                >
                  <IconPlusSign className="h-3 w-3 text-neutral-500" />
                </button>
              );
            })()}

            {stops.map((s, i) => (
              <div
                key={i}
                className={clsx(
                  'absolute top-1/2 h-3 w-3 -translate-y-1/2 cursor-ew-resize rounded-full outline outline-white',
                  // Make the clicked/selected stop persistently highlighted
                  activeStopIdx === i
                    ? 'shadow-[0px_0px_6px_0px_rgba(0,0,0,0.60)] outline-3'
                    : 'shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] outline-1',
                )}
                style={{
                  // Align knob so the circle touches edges without protruding
                  left: `calc(${s.position} * (100% - 12px))`,
                }}
                onMouseEnter={() => setHoveredStopIdx(i)}
                onMouseLeave={() => setHoveredStopIdx((prev) => (prev === i ? null : prev))}
                onMouseDown={(e) => {
                  setActiveStopIdx(i); // select stop on click
                  setDraggingStop(i); // optional: keep dragging behavior
                  const startX = e.clientX ?? 0;
                  const move = (ev: MouseEvent) => {
                    const el = gradientRef.current;
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    const x = Math.max(0, Math.min(rect.width, (ev.clientX ?? startX) - rect.left));
                    const p = Math.max(0, Math.min(1, x / rect.width));
                    setStops((prev) => {
                      const copy = [...prev];
                      copy[i] = { ...copy[i], position: p };
                      return copy;
                    });
                    // Broadcast live gradient changes with fresh data
                    const freshStops = stops.map((s, idx) =>
                      idx === i ? { ...s, position: p } : s,
                    );
                    handleBroadcast({
                      mode: 'gradient',
                      gradient: {
                        type: gradientType,
                        angle: gradientAngle,
                        stops: freshStops.map((s) => ({
                          position: s.position,
                          hexa: Color(s.hexa).hexa().toUpperCase(),
                        })),
                      },
                    });
                  };
                  const up = (ev: MouseEvent) => {
                    document.removeEventListener('mousemove', move);
                    document.removeEventListener('mouseup', up);
                    setDraggingStop(null);
                    const el = gradientRef.current;
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    const x = Math.max(0, Math.min(rect.width, (ev.clientX ?? startX) - rect.left));
                    const p = Math.max(0, Math.min(1, x / rect.width));
                    skipNextGradientBroadcastRef.current = true;
                    // Broadcast live gradient changes with fresh data
                    const freshStops = stops.map((s, idx) =>
                      idx === i ? { ...s, position: p } : s,
                    );
                    handleBroadcastComplete({
                      mode: 'gradient',
                      gradient: {
                        type: gradientType,
                        angle: gradientAngle,
                        stops: freshStops.map((s) => ({
                          position: s.position,
                          hexa: Color(s.hexa).hexa().toUpperCase(),
                        })),
                      },
                    });
                  };
                  document.addEventListener('mousemove', move);
                  document.addEventListener('mouseup', up);
                }}
                title={`Stop ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Value and opacity row */}
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <div className="inline-flex h-8 items-center justify-between gap-2 self-stretch">
          <SelectField
            size="none"
            text=""
            placeholder={format}
            className="w-[65px]"
            classNameTrigger="w-[65px]"
            classNameValue="text-button-sm"
            defaultValue={format}
            truncate={false}
            onValueChange={(v) => {
              const f = (v as 'HEX' | 'RGB' | 'HSL') || 'HEX';
              setFormat(f);
              // update displays immediately
              if (f === 'HEX') setTextValue(formatColor(currentColor, 'HEX'));
              if (f === 'RGB') {
                const { r = 0, g = 0, b = 0 } = currentColor.rgb().object();
                setRgbValues({
                  r: String(Math.round(r)),
                  g: String(Math.round(g)),
                  b: String(Math.round(b)),
                });
              }
              if (f === 'HSL') {
                const hsl = currentColor.hsl().object();
                setHslValues({
                  h: String(Math.round(hsl.h ?? 0)),
                  s: String(Math.round(hsl.s ?? 0)),
                  l: String(Math.round(hsl.l ?? 0)),
                });
              }
            }}
          >
            <SelectItem value="HEX">HEX</SelectItem>
            <SelectItem value="RGB">RGB</SelectItem>
            <SelectItem value="HSL">HSL</SelectItem>
          </SelectField>

          <div
            className={clsx(
              // kill transitions to avoid janky animation while switching formats
              'group flex max-w-[152px] items-center justify-start rounded-md outline outline-transparent !transition-none focus-within:outline-indigo-500',
            )}
          >
            {/* Left side: value inputs */}
            {format === 'HEX' ? (
              <div
                key="HEX"
                className="flex h-8 w-full items-center justify-start gap-1 rounded-l-md bg-neutral-50 p-2 !transition-none"
              >
                {(() => {
                  const isValidHexValue = isValidHex(textValue);

                  if (isValidHexValue) {
                    return (
                      <div
                        className="h-4.5 min-h-4.5 w-4.5 min-w-4.5 rounded border border-neutral-200"
                        style={{ backgroundColor: `#${textValue.replace('#', '')}` }}
                      />
                    );
                  } else {
                    return (
                      <div className="h-4.5 min-h-4.5 w-4.5 min-w-4.5 overflow-hidden rounded outline outline-offset-[-1px] outline-neutral-200">
                        <img
                          src={FillPlaceholder}
                          className="h-full w-full object-cover"
                          alt="Invalid color"
                        />
                      </div>
                    );
                  }
                })()}
                <Input
                  className="w-full pr-2 pl-2 font-medium !transition-none"
                  placeholder="RRGGBB"
                  value={textValue}
                  onFocus={() => {
                    inputFocusedRef.current = true;
                  }}
                  onBlur={() => {
                    inputFocusedRef.current = false;
                    setTextValue(formatColor(currentColor, 'HEX'));
                  }}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setTextValue(v);
                  }}
                  onChangeComplete={() => {
                    tryApplyText(textValue);
                  }}
                />
              </div>
            ) : format === 'RGB' ? (
              <div key="RGB" className="flex items-center justify-start !transition-none">
                <Input
                  className="h-8 w-9 rounded-tl-md rounded-r-none rounded-bl-md border-r border-neutral-100 bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={rgbValues.r}
                  onFocus={() => {
                    rgbFocusedRef.current.r = true;
                  }}
                  onBlur={() => {
                    rgbFocusedRef.current.r = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setRgbValues((prev) => ({ ...prev, r: v }));
                  }}
                  onChangeComplete={() => {
                    tryApplyRgb(rgbValues.r, rgbValues.g, rgbValues.b);
                  }}
                />
                <Input
                  className="h-8 w-9 rounded-none border-r border-neutral-100 bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={rgbValues.g}
                  onFocus={() => {
                    rgbFocusedRef.current.g = true;
                  }}
                  onBlur={() => {
                    rgbFocusedRef.current.g = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setRgbValues((prev) => ({ ...prev, g: v }));
                  }}
                  onChangeComplete={() => {
                    tryApplyRgb(rgbValues.r, rgbValues.g, rgbValues.b);
                  }}
                />
                <Input
                  className="h-8 w-9 rounded-none bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={rgbValues.b}
                  onFocus={() => {
                    rgbFocusedRef.current.b = true;
                  }}
                  onBlur={() => {
                    rgbFocusedRef.current.b = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setRgbValues((prev) => ({ ...prev, b: v }));
                  }}
                  onChangeComplete={() => {
                    tryApplyRgb(rgbValues.r, rgbValues.g, rgbValues.b);
                  }}
                />
              </div>
            ) : (
              // HSL
              <div key="HSL" className="flex items-center justify-start !transition-none">
                <Input
                  className="h-8 w-9 rounded-r-none border-r border-neutral-100 bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={hslValues.h}
                  onFocus={() => {
                    hslFocusedRef.current.h = true;
                  }}
                  onBlur={() => {
                    hslFocusedRef.current.h = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setHslValues((prev) => ({ ...prev, h: v }));
                  }}
                  onChangeComplete={() => {
                    tryApplyHsl(hslValues.h, hslValues.s, hslValues.l);
                  }}
                />
                <Input
                  className="h-8 w-9 rounded-none border-r border-neutral-100 bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={hslValues.s}
                  onFocus={() => {
                    hslFocusedRef.current.s = true;
                  }}
                  onBlur={() => {
                    hslFocusedRef.current.s = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setHslValues((prev) => ({ ...prev, s: v }));
                    tryApplyHsl(hslValues.h, v, hslValues.l);
                  }}
                  onChangeComplete={() => {
                    tryApplyHsl(hslValues.h, hslValues.s, hslValues.l);
                  }}
                />
                <Input
                  className="h-8 w-9 rounded-none bg-neutral-50 p-1.5 text-center text-xs font-medium text-neutral-900 !transition-none group-focus-within:border-indigo-500"
                  value={hslValues.l}
                  onFocus={() => {
                    hslFocusedRef.current.l = true;
                  }}
                  onBlur={() => {
                    hslFocusedRef.current.l = false;
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                    setHslValues((prev) => ({ ...prev, l: v }));
                  }}
                  onChangeComplete={() => {
                    tryApplyHsl(hslValues.h, hslValues.s, hslValues.l);
                  }}
                />
              </div>
            )}

            {/* Right side: opacity input (unchanged) */}
            <div className="w-11 rounded-md">
              <Input
                className={clsx(
                  'w-11 rounded-l-none border-l border-neutral-100 pr-1.5 pl-1.5 font-medium !transition-none group-focus-within:border-indigo-500',
                )}
                value={`${Math.round(alpha * 100)}%`}
                onChange={(e) => {
                  const n = clamp(parseInt(e.target.value.replace('%', '') || '0', 10), 0, 100);
                  setCurrentOpacity(n);
                  const newAlpha = n / 100;
                  setAlpha(newAlpha);
                  updateActiveStopColor(hue, sat, val, newAlpha);
                }}
                onChangeComplete={() => {
                  handleBroadcastComplete({ opacity: Math.round(alpha * 100) });
                }}
              />
            </div>
          </div>
        </div>

        {/* Extra selects for gradient mode */}
        {currentMode === 'gradient' && (
          <div className="flex flex-col items-start justify-start gap-2 self-stretch">
            <div className="inline-flex h-8 items-center justify-between self-stretch">
              <div className="text-button-sm leading-none font-medium text-neutral-500">Type</div>
              {gradientType === 'Linear' && (
                <NumberField
                  min={0}
                  suffix="°"
                  max={360}
                  className="mr-2 ml-auto"
                  value={gradientAngle}
                  onChange={(value) => {
                    // Accept numeric + deg suffix
                    setGradientAngle(value);
                    handleBroadcast({
                      angle: value,
                    });
                  }}
                  onChangeComplete={({ target }) => {
                    const value = parseInt(
                      (target as HTMLInputElement).value || gradientAngle.toString(),
                      10,
                    );

                    setGradientAngle(value);
                    handleBroadcastComplete({
                      angle: value,
                    });
                  }}
                />
              )}
              <SelectField
                size="large"
                text=""
                placeholder="Linear"
                defaultValue={gradientType}
                side="bottom"
                avoidCollisions={false}
                onValueChange={(v) => {
                  const nextType = (v as 'Linear' | 'Radial') || 'Linear';
                  setGradientType(nextType);
                  if (currentMode === 'gradient') {
                    skipNextGradientBroadcastRef.current = true;
                    handleBroadcastComplete({
                      mode: 'gradient',
                      gradient: {
                        type: nextType,
                        stops: stops.map((s) => ({
                          position: s.position,
                          hexa: Color(s.hexa).hexa().toUpperCase(),
                        })),
                      },
                    });
                  }
                }}
              >
                <SelectItem value="Linear">Linear</SelectItem>
                <SelectItem value="Radial">Radial</SelectItem>
              </SelectField>
            </div>
          </div>
        )}

        {/* Palette + Style select */}
        {palettesState.length > 0 && (
          <div className="flex flex-col items-start justify-start gap-2 self-stretch">
            <div className="inline-flex h-8 items-center justify-between self-stretch">
              <div className="text-button-sm leading-none font-medium text-neutral-500">
                Palette
              </div>

              <div className="w-[152px]">
                <SelectField
                  size="large"
                  text=""
                  defaultValue={palette}
                  className="w-full"
                  classNameTrigger="w-full"
                  onValueChange={(v) => setPalette(v)}
                >
                  {palettesState.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectField>
              </div>
            </div>

            <div className="inline-flex flex-wrap items-center justify-start gap-1.5 self-stretch">
              {visiblePaletteColors.map((c, idx) => {
                // Skip gradient colors if the picker is not configured to allow them
                if (c.gradient && !hasGradient) {
                  return;
                }

                const style = c.gradient
                  ? {
                      backgroundImage:
                        c.gradient.type === 'Linear'
                          ? `linear-gradient(${c.gradient.angle || 0}deg, ${c.gradient.stops
                              .map(
                                (s) => `${Color(s.hexa).string()} ${Math.round(s.position * 100)}%`,
                              )
                              .join(', ')})`
                          : `radial-gradient(circle, ${c.gradient.stops
                              .map(
                                (s) => `${Color(s.hexa).string()} ${Math.round(s.position * 100)}%`,
                              )
                              .join(', ')})`,
                    }
                  : c.hex
                    ? { backgroundColor: `#${c.hex}` }
                    : isColorReference(c.reference)
                      ? {
                          backgroundColor: resolveReferenceColor(c.reference),
                        }
                      : undefined;

                return (
                  <button
                    key={`${c.name}-${idx}`}
                    className={clsx(
                      'h-5 min-h-5 w-5 min-w-5 cursor-pointer rounded border border-neutral-200',
                      c.className,
                    )}
                    style={style}
                    onClick={(e) => {
                      // If palette item is a gradient, load it into the editor
                      if (c.gradient) {
                        const gradientAngle = c.gradient.angle || 0;
                        const gradientType = c.gradient.type;
                        const newStops = c.gradient.stops.map((s) => ({ ...s }));
                        setCurrentMode('gradient');
                        setGradientType(gradientType);
                        setGradientAngle(gradientAngle);
                        setStops(newStops);
                        setPreviewStops(newStops);
                        setActiveStopIdx(0);
                        // Explicitly broadcast the selected gradient to avoid stale state
                        skipNextGradientBroadcastRef.current = true;
                        handleBroadcastComplete({
                          mode: 'gradient',
                          reference: c.reference,
                          gradient: {
                            type: gradientType,
                            angle: gradientAngle,
                            stops: newStops.map((s) => ({
                              position: s.position,
                              hexa: Color(s.hexa).hexa().toUpperCase(),
                            })),
                          },
                        });
                        return;
                      }

                      // Solid color selected - switch to solid mode
                      const bg = getComputedStyle(
                        e.currentTarget as HTMLButtonElement,
                      ).backgroundColor;
                      try {
                        const parsed = Color(bg);
                        const hsv = parsed.hsv().object();
                        const nextHex = parsed.hex().replace('#', '').toUpperCase();
                        setHue(typeof hsv.h === 'number' && !isNaN(hsv.h) ? hsv.h : 0);
                        setSat((typeof hsv.s === 'number' ? hsv.s : 100) / 100 || 0);
                        setVal((typeof hsv.v === 'number' ? hsv.v : 100) / 100 || 0);
                        setCurrentHex(nextHex);
                        // Always switch to solid mode when a solid color is selected
                        setCurrentMode('solid');
                        handleBroadcastComplete({
                          mode: 'solid',
                          hex: nextHex,
                          reference: c.reference,
                        });
                      } catch {
                        // ignore
                      }
                    }}
                  />
                );
              })}
              {/* Add tile - only if allowed for the selected palette; adds current solid/gradient into the palette */}
              {canAddToSelectedPalette && (
                <button
                  type="button"
                  aria-label="Add to palette"
                  className="bg-neutral-0 inline-flex h-5 w-5 cursor-pointer flex-col items-center justify-start gap-2"
                  onClick={() => {
                    if (!canAddToSelectedPalette) return;
                    if (currentMode === 'gradient') {
                      const candidate = {
                        type: gradientType,
                        stops: stops.map((s) => ({ ...s, hexa: normalizeHexa(s.hexa) })),
                      };
                      setPalettesState((prev) => {
                        const idx = prev.findIndex((p) => p.name === palette);
                        if (idx === -1) return prev;
                        const list = prev[idx].colors || [];
                        const exists = list.some(
                          (c: PaletteColor) => !!c.gradient && sameGradient(c.gradient, candidate),
                        );
                        if (exists) return prev;
                        const nextColor: PaletteColor = {
                          name: `custom-gradient-${list.length + 1}`,
                          gradient: candidate,
                        };
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], colors: [...list, nextColor] };
                        return copy;
                      });
                    } else {
                      const hexa = normalizeHexa(currentColor.hexa());
                      setPalettesState((prev) => {
                        const idx = prev.findIndex((p) => p.name === palette);
                        if (idx === -1) return prev;
                        const list = prev[idx].colors || [];
                        const exists = list.some(
                          (c: PaletteColor) => !c.gradient && c.hex && sameSolid(c.hex, hexa),
                        );
                        if (exists) return prev;
                        const nextColor: PaletteColor = {
                          name: `custom-${list.length + 1}`,
                          hex: hexa,
                        };
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], colors: [...list, nextColor] };
                        return copy;
                      });
                    }
                  }}
                >
                  <div className="inline-flex h-full w-full items-center justify-center gap-2 rounded border border-neutral-200">
                    <IconAdd className="h-3 w-3 text-neutral-500" />
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
